import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { createPersonLegal } from '@dealstack/frontend/app/services/Person.service.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect } from 'react';

export enum CREATE_PERSON_LEGAL_FORM_VALUES {
	NAME = 'name',
}

interface CreatePersonLegalForm {
	[CREATE_PERSON_LEGAL_FORM_VALUES.NAME]: string;
}

export const CreatePersonLegalShape = z.object({
	[CREATE_PERSON_LEGAL_FORM_VALUES.NAME]: z.string().nonempty({ message: 'Name is required' }),
});

export const useCreatePersonLegalController = (
	onCreateSuccess: (createdEntityId: string, withClose?: boolean) => void,
	dealId: string,
	setComputedName: (name: string) => void
) => {
	const createLegalPersonRequest = useMutation({
		mutationKey: [API_PATHS.PERSON_DEAL_DEALID_PERSON_CREATE_LEGAL_POST, { dealId }],
		mutationFn: createPersonLegal,
	});

	const createPersonLegalForm = useForm<CreatePersonLegalForm>({
		mode: 'onSubmit',
		resolver: zodResolver(CreatePersonLegalShape),
		defaultValues: {
			[CREATE_PERSON_LEGAL_FORM_VALUES.NAME]: '',
		},
	});

	const watchedName = createPersonLegalForm.watch(CREATE_PERSON_LEGAL_FORM_VALUES.NAME);

	useEffect(() => {
		setComputedName(watchedName);
	}, [watchedName, setComputedName]);

	const onSubmit = async (formValues: CreatePersonLegalForm, resetFormOnSuccess?: boolean) => {
		const { name } = formValues;
		createLegalPersonRequest.mutate(
			{ dealId, createArgs: { name } },
			{
				onSuccess: (createdEntity) => {
					if (resetFormOnSuccess) {
						createPersonLegalForm.reset();
					}

					onCreateSuccess(createdEntity.id, !resetFormOnSuccess);
				},
			}
		);
	};

	return {
		createPersonLegalForm,
		onSubmit,
		isLoading: createLegalPersonRequest.isPending,
	};
};
